import { useEffect, useMemo, useState } from 'react'
import { Flag, LanguageFlag } from '@/shared/places/Flag'
import { usePlaces, type Place } from '@/shared/places/customerFacts'
import { nf } from '@/shared/util/numbers'
import {
  enrichProCompanies,
  enrichProMembers,
  loadProCompanies,
  type ProBillingProfile,
  type ProCompany,
  type ProMember,
} from './repository/proCompaniesRepository'
import { formatDate } from '@/shared/util/dates'
import { NotesField } from '@/shared/notes/NotesField'
import { InfoTip } from '@/shared/ui/InfoTip'
import { ProductAccessError } from '@/features/auth/ProductAccessError'
import { useAuth } from '@/features/auth/AuthProvider'
import { PRO_STATES, STATE_OF, companyState, lastActivityAt, type ProState } from './proCompanyState'
import { useLeads } from '@/features/leads/hooks/useLeads'
import { useCrmSettings } from '@/features/settings/hooks/useCrmSettings'
import type { Lead } from '@/types/crm'
import { linkedCompanyId } from './proLeadMatch'
import { ProLeadPanel } from './ProLeadPanel'


type View = 'companies' | 'guides'
type SortKey =
  | 'name' | 'company' | 'createdAt' | 'lastSignInAt'
  | 'routes' | 'tours' | 'plan' | 'onboarding'

/**
 * Skida PRO: the companies, and the guides inside them.
 *
 * Two views over one load. The company view answers "who are these companies
 * and who is in them"; the guide view flattens the same data so a single guide
 * can be found without knowing which company they belong to.
 *
 * Everything is loaded whole — 86 companies and 121 memberships — so sorting
 * and searching here are honest across the whole set, unlike the consumer table
 * where they can only act on a page.
 */
export function ProCompanies() {
  const [companies, setCompanies] = useState<ProCompany[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { productEmail } = useAuth()
  /** Why routes and tours are missing, when they are — decision 3. */
  const [countsError, setCountsError] = useState<string | null>(null)
  const [view, setView] = useState<View>('companies')
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [sortKey, setSortKey] = useState<SortKey>('lastSignInAt')
  const [asc, setAsc] = useState(false)
  // `?company=<id>` opens that company — the Skida PRO card on a lead links
  // here (22 Oct 2026). Taken out of the address once read.
  const [focusId] = useState(() => {
    const id = new URLSearchParams(window.location.search).get('company')
    if (id) window.history.replaceState(null, '', window.location.pathname)
    return id
  })
  useEffect(() => {
    if (!focusId || loading) return
    setOpen((prev) => new Set(prev).add(focusId))
    requestAnimationFrame(() =>
      document.getElementById(`procomp-${focusId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
    )
  }, [focusId, loading])

  /** The state card pressed above the table, if any — it filters both views. */
  const [stateFilter, setStateFilter] = useState<ProState | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setCountsError(null)
    loadProCompanies()
      .then(async (list) => {
        if (cancelled) return
        setCompanies(list)
        setLoading(false)
        // Counts arrive after the names: the company totals in one pass over
        // each collection, the per-guide attribution one aggregation each.
        const [withTotals, enriched] = await Promise.all([
          // A failure here is the counts, not the companies: the names are
          // already on screen and must stay there. What it must not do is leave
          // 86 rows reading "—" as though nobody had built anything.
          enrichProCompanies(list).catch((err: unknown) => {
            setCountsError(err instanceof Error ? err.message : 'the read was refused')
            return list
          }),
          enrichProMembers(list.flatMap((c) => c.members)),
        ])
        if (cancelled) return
        const patch = new Map(enriched.map((m) => [`${m.companyId}_${m.uid}`, m]))
        setCompanies(
          withTotals.map((c) => ({
            ...c,
            members: c.members.map((m) => patch.get(`${m.companyId}_${m.uid}`) ?? m),
          })),
        )
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Could not read Skida PRO companies.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Read again when the product account changes, so a reconnect from the
    // error message shows the companies without a reload.
  }, [productEmail])

  /**
   * The CRM side — 21 Oct 2026. Each lead that is linked to a company, by the
   * Skida PRO sync or by a rep, keyed by the company it is linked to.
   */
  const { leads } = useLeads()
  const settings = useCrmSettings()
  const leadByCompany = useMemo(() => {
    const m = new Map<string, Lead>()
    for (const l of leads) {
      const id = linkedCompanyId(l)
      if (id && !m.has(id)) m.set(id, l)
    }
    return m
  }, [leads])
  const stageLabel = (key: string) =>
    [...settings.stages, ...settings.outcomes].find((s) => s.key === key)?.label ?? key

  /** Each company's state, worked out once from the rows the table shows. */
  const stateById = useMemo(() => {
    const now = Date.now()
    return new Map(companies.map((c) => [c.id, companyState(c, now)]))
  }, [companies])
  const stateCounts = useMemo(() => {
    const n = Object.fromEntries(PRO_STATES.map((s) => [s.key, 0])) as Record<ProState, number>
    for (const st of stateById.values()) if (st) n[st]++
    return n
  }, [stateById])
  const stillCounting = [...stateById.values()].some((st) => st === null)

  const inState = (companyId: string) => !stateFilter || stateById.get(companyId) === stateFilter
  const guides = useMemo(() => companies.flatMap((c) => c.members), [companies])
  // Country and language per guide, from RevenueCat — 28 Oct 2026.
  const places = usePlaces(guides.map((m) => m.uid))
  const needle = term.trim().toLowerCase()

  const filteredCompanies = useMemo(() => {
    const scoped = companies.filter((c) => inState(c.id))
    if (!needle) return scoped
    return scoped.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        c.email.toLowerCase().includes(needle) ||
        c.members.some(
          (m) => m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle),
        ),
    )
    // `inState` reads `stateFilter` and `stateById`, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, needle, stateFilter, stateById])

  const filteredGuides = useMemo(() => {
    const scoped = guides.filter((m) => inState(m.companyId))
    const rows = needle
      ? scoped.filter(
          (m) =>
            m.name.toLowerCase().includes(needle) ||
            m.email.toLowerCase().includes(needle) ||
            m.companyName.toLowerCase().includes(needle),
        )
      : scoped
    const sign = asc ? 1 : -1
    const val = (m: ProMember): string | number | null => {
      switch (sortKey) {
        case 'name':
          return (m.name || m.email).toLowerCase()
        case 'company':
          return m.companyName.toLowerCase()
        case 'plan':
          // Ordered by how far along they are, so a sort groups the base.
          return { registered: 0, expired: 1, granted: 2, paid: 3 }[m.access]
        case 'onboarding':
          // Unfinished first when descending: those are the ones to chase.
          return m.onboardingCompleted === null ? null : m.onboardingCompleted ? 1 : 0
        case 'routes':
          return m.routes
        case 'tours':
          return m.tours
        default:
          return m[sortKey]
      }
    }
    // Blanks sink either way — an unsynced sign-in is not "oldest".
    return [...rows].sort((a, b) => {
      const av = val(a)
      const bv = val(b)
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign
      return String(av).localeCompare(String(bv)) * sign
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guides, needle, sortKey, asc, stateFilter, stateById])

  const synced = guides.filter((m) => m.lastSignInAt).length

  function sortBy(k: SortKey) {
    if (sortKey === k) setAsc(!asc)
    else {
      setSortKey(k)
      setAsc(k === 'name' || k === 'company')
    }
  }
  const caret = (k: SortKey) =>
    sortKey === k ? <span className="usertable__sortcaret">{asc ? '▲' : '▼'}</span> : null

  return (
    <>
    {/*
      The state of every company, counted from the same rows as the table
      below — 21 Oct 2026. Pressing a card filters the table to it.
    */}
    <div className="dash__kpis" data-onb="pro-kpis">
      {PRO_STATES.map((st) => (
        <div className="dash__kpi" key={st.key}>
          <button
            className="dash__kpimain procomp__statebtn"
            aria-pressed={stateFilter === st.key}
            onClick={() => setStateFilter((f) => (f === st.key ? null : st.key))}
          >
            <div className="dash__kpilabel">{st.label}</div>
            <div className="dash__kpivalue" style={{ color: st.color }}>
              {loading ? '…' : nf(stateCounts[st.key])}
            </div>
            <div className="dash__kpisub">
              {stateFilter === st.key ? 'showing these — press to clear' : 'companies'}
            </div>
          </button>
          <span className="dash__kpiinfo">
            <InfoTip label={st.label}>{st.hint}</InfoTip>
          </span>
        </div>
      ))}
    </div>
    {!loading && stillCounting && !countsError && (
      <p className="micro muted procomp__counting">Counting routes and tours — the cards settle in a moment.</p>
    )}
    <section className="card procomp">
      <div className="usertable__head">
        <div className="card-label">
          Skida PRO — companies and guides{' '}
          {!loading && `(${nf(companies.length)} companies, ${nf(guides.length)} memberships)`}
        </div>
        <div className="usertable__controls">
          <input
            className="input usertable__search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Company, guide or email…"
            aria-label="Search Skida PRO"
          />
          <button className="chip" aria-pressed={view === 'companies'} onClick={() => setView('companies')}>
            Companies
          </button>
          <button className="chip" aria-pressed={view === 'guides'} onClick={() => setView('guides')}>
            Guides
          </button>
        </div>
      </div>

      {error && <ProductAccessError what="the Skida PRO companies" error={error} />}
      {loading && <div className="empty-note dash__empty">Loading companies…</div>}

      {!loading && view === 'companies' && countsError && (
        <p className="empty-note dash__empty">
          Routes and tours could not be read from the product project, so every row below shows a
          dash rather than a count — {countsError}. The figures exist: 546 routes and 61 tours
          carry a company. This is a permission on the product project, not a missing feature.
        </p>
      )}

      {!loading && view === 'companies' && (
        <div className="procomp__list">
          {filteredCompanies.map((c) => {
            const isOpen = open.has(c.id)
            const admins = c.members.filter((m) => m.role === 'admin')
            const onb = companyOnboarding(c.members)
            return (
              <div className="procomp__co" key={c.id} id={`procomp-${c.id}`}>
                <button
                  className="procomp__cohead"
                  onClick={() =>
                    setOpen((prev) => {
                      const next = new Set(prev)
                      if (next.has(c.id)) next.delete(c.id)
                      else next.add(c.id)
                      return next
                    })
                  }
                  aria-expanded={isOpen}
                >
                  {c.logoUrl ? (
                    <img className="procomp__logo" src={c.logoUrl} alt="" loading="lazy" />
                  ) : (
                    <span className="procomp__logo procomp__logo--none" aria-hidden="true">
                      {c.name.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className="procomp__coname">
                    <span className="procomp__title">{c.name}</span>
                    {c.tagline && <span className="procomp__tagline">{c.tagline}</span>}
                  </span>
                  <StateBadge state={stateById.get(c.id) ?? null} />
                  {leadByCompany.get(c.id) ? (
                    <span className="pill procomp__lead" title="The CRM lead this company is linked to">
                      {leadByCompany.get(c.id)!.name}
                    </span>
                  ) : (
                    <span className="procomp__meta procomp__nolead">Not in CRM</span>
                  )}
                  <span className="procomp__meta">
                    {c.members.length} {c.members.length === 1 ? 'guide' : 'guides'}
                  </span>
                  <span className="procomp__meta procomp__work">
                    {c.routes ?? '—'} routes · {c.tours ?? '—'} tours
                  </span>
                  <CompanyOnboardingBadge onb={onb} />
                  <span className="procomp__meta">{c.createdAt ? formatDate(c.createdAt) : '—'}</span>
                  {/* Always a cell, filled or not: the caret is the last column,
                      and a conditional child before it moved everything left on
                      the two rows in three that have no billing profile. */}
                  {c.billing ? (
                    <span className="pill procomp__billing">
                      Billing{c.billing.countryCode ? ` · ${c.billing.countryCode}` : ''}
                    </span>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                  <span className="procomp__caret">{isOpen ? '▲' : '▼'}</span>
                </button>

                {isOpen && (
                  <div className="procomp__detail">
                    {/*
                      Everything the customer told us, on the screen where we
                      decide whether to ring them.

                      Sales asked for this in as many words: qualify why a
                      company stopped and how we can help. The company record is
                      small — a name, a tagline, an email and, if they got that
                      far, a billing profile — so all of it fits, and the ones
                      that are missing are the finding. A company with a filled
                      billing address that never paid got much further than one
                      that never left the workspace step.
                    */}
                    <div className="procomp__profile">
                      <div className="procomp__facts">
                        {c.email && (
                          <span>
                            <strong>Email</strong>{' '}
                            <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>
                              {c.email}
                            </a>
                          </span>
                        )}
                        {c.tagline && (
                          <span>
                            <strong>Tagline</strong> {c.tagline}
                          </span>
                        )}
                        {c.claimCode && (
                          <span>
                            <strong>Join code</strong> <code>{c.claimCode}</code>
                          </span>
                        )}
                        <span>
                          <strong>Created</strong> {c.createdAt ? formatDate(c.createdAt) : '—'}
                          {(c.createdByName || c.createdByEmail) &&
                            ` by ${c.createdByName || c.createdByEmail}`}
                        </span>
                        <span title="The latest of: the company record changing, a guide joining, signing in or moving through onboarding, and a route or tour being made or edited.">
                          <strong>Last activity</strong>{' '}
                          {(() => {
                            const at = lastActivityAt(c)
                            return at ? formatDate(at) : '—'
                          })()}
                        </span>
                        {c.updatedAt && (
                          <span title="The last time anybody changed the company record itself.">
                            <strong>Last edited</strong> {formatDate(c.updatedAt)}
                          </span>
                        )}
                        <span>
                          <strong>Onboarding</strong> <OnboardingSummary onb={onb} />
                        </span>
                        {admins.length > 0 && (
                          <span>
                            <strong>Admin</strong>{' '}
                            {admins.map((a) => a.name || a.email || a.uid).join(', ')}
                          </span>
                        )}
                        {(c.unattributedRoutes ?? 0) + (c.unattributedTours ?? 0) > 0 && (
                          <span>
                            <strong>Unattributed</strong> {c.unattributedRoutes ?? 0} routes,{' '}
                            {c.unattributedTours ?? 0} tours carry no guide
                          </span>
                        )}
                      </div>

                      <BillingCard billing={c.billing} />
                    </div>

                    {/*
                      AVV-2026-003, and decision 91.

                      Sales asked for *"Notes m/ New note og time stamp, og gjerne
                      navn på den som har skrevet notatet"* on a company. Leads
                      already had the activity log; a Skida PRO company had
                      nothing, which is the only company record in the tool where
                      the report was true.

                      The company itself lives in the **product** project and is
                      read-only from here (98), so the notes live in the CRM's own
                      collection keyed by the product company id. Nothing is
                      written across the boundary.
                    */}
                    <ProLeadPanel
                      company={c}
                      linked={leadByCompany.get(c.id) ?? null}
                      leads={leads}
                      stageLabel={stageLabel}
                      firstStage={settings.stages[0]?.key ?? 'unverified'}
                    />

                    <div className="procomp__notes">
                      <NotesField
                        kind="proCompany"
                        recordId={c.id}
                        legend="Notes"
                        hint="Internal to the CRM. The company record itself belongs to the product project and is never written to from here."
                      />
                    </div>

                    <div className="procomp__grid procomp__gridhead">
                      <div>Guide</div>
                      <div title="Where RevenueCat last saw the account">Country</div>
                      <div title="The language Skida writes in for that country">Language</div>
                      <div>Role</div>
                      <div>Joined</div>
                      <div>Registered</div>
                      <div>Last sign-in</div>
                      <div title="Routes tagged with this guide. Ownership sits on the company, so these do not add up to the company total.">
                        Routes*
                      </div>
                      <div title="Tours tagged with this guide.">Tours*</div>
                      <div>Onboarding</div>
                      <div>Subscription</div>
                    </div>
                    {c.members.map((m) => (
                      <MemberRow key={m.uid} m={m} place={places.get(m.uid)} />
                    ))}
                    {c.members.length === 0 && (
                      <div className="empty-note procomp__empty">
                        Nobody has joined this company yet.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {filteredCompanies.length === 0 && (
            <div className="empty-note dash__empty">No company or guide matches that.</div>
          )}
        </div>
      )}

      {!loading && view === 'guides' && (
        <div className="usertable__scroll">
          <div className="procomp__grid procomp__gridhead procomp__grid--guides">
            <button className="usertable__th" onClick={() => sortBy('name')}>
              Guide {caret('name')}
            </button>
            <div title="Where RevenueCat last saw the account">Country</div>
            <div title="The language Skida writes in for that country">Language</div>
            <button className="usertable__th" onClick={() => sortBy('company')}>
              Company {caret('company')}
            </button>
            <div>Role</div>
            <button className="usertable__th" onClick={() => sortBy('createdAt')}>
              Registered {caret('createdAt')}
            </button>
            <button className="usertable__th" onClick={() => sortBy('lastSignInAt')}>
              Last sign-in {caret('lastSignInAt')}
            </button>
            <button
              className="usertable__th usertable__th--num"
              onClick={() => sortBy('routes')}
              title="Routes tagged with this guide. Ownership sits on the company, so these do not add up to the company total."
            >
              Routes* {caret('routes')}
            </button>
            <button
              className="usertable__th usertable__th--num"
              onClick={() => sortBy('tours')}
              title="Tours tagged with this guide."
            >
              Tours* {caret('tours')}
            </button>
            <button className="usertable__th" onClick={() => sortBy('onboarding')}>
              Onboarding {caret('onboarding')}
            </button>
            <button className="usertable__th" onClick={() => sortBy('plan')}>
              Subscription {caret('plan')}
            </button>
          </div>
          {filteredGuides.map((m) => (
            <MemberRow key={`${m.companyId}_${m.uid}`} m={m} place={places.get(m.uid)} showCompany />
          ))}
          {filteredGuides.length === 0 && (
            <div className="empty-note dash__empty">No guide matches that.</div>
          )}
        </div>
      )}

      <div className="micro usertable__note">
        {/* Corrected in the 14 Aug round. The note used to describe the consumer
            app's route collections and the automatic bookmarks list, which is a
            different product from the one this column counts — the code was
            always reading skidaProTours. The bookmark figure now has its own
            card on the Skida app dashboard. */}
        “Tours” counts Skida PRO tours (<code>skidaProTours</code>) tagged with this guide, and
        nothing else. The consumer app’s saved routes and bookmark collections are a separate
        figure, on the Skida app dashboard.{' '}
        {synced === 0 ? (
          <>
            <strong>Last sign-in is not synced yet.</strong> It is not in Firestore at all: Firebase
            Auth holds it as <code>lastSignInTime</code> and only the Admin SDK can read it. Run{' '}
            <code>scripts/syncProMembers.ts --commit</code> to mirror it in, and the column fills.
          </>
        ) : (
          <>
            Last sign-in is mirrored from Firebase Auth by <code>scripts/syncProMembers.ts</code> —{' '}
            {nf(synced)} of {nf(guides.length)} synced. A blank means the sync has not seen that
            guide, never that they have not signed in.
          </>
        )}
      </div>
    </section>
    </>
  )
}

/**
 * The Skida PRO onboarding wizard, in the order a guide walks it.
 *
 * The keys are the product's; the labels are ours, because `workspace-create`
 * is not what you say on a call. An unknown key renders as itself rather than
 * as a blank — a step added to the product tomorrow must not read as "no step".
 */
const STEPS: { key: string; label: string; hint: string }[] = [
  {
    key: 'workspace-choice',
    label: 'Workspace choice',
    hint: 'Stopped before choosing whether to join a company or start one.',
  },
  {
    key: 'workspace-create',
    label: 'Create workspace',
    hint: 'Began setting the company up and never finished it.',
  },
  {
    key: 'profile',
    label: 'Guide profile',
    hint: 'The company exists; the guide profile was never filled in.',
  },
  {
    key: 'paywall',
    label: 'Paywall',
    hint: 'Reached the payment step and stopped there — the warmest signal on this screen.',
  },
  {
    key: 'complete',
    label: 'Final step',
    hint: 'On the last screen with the wizard never marked finished.',
  },
]

const stepOf = (key: string) => STEPS.find((s) => s.key === key)
const stepLabel = (key: string) => stepOf(key)?.label ?? key

/**
 * Where a company is with onboarding, as one fact.
 *
 * A company is not onboarded or not — it is a handful of guides at different
 * points, and the collapsed row has space for one line. So: how many are
 * through, where the rest are stuck, and since when.
 *
 * The step named is the one most of the unfinished sit on, ties going to the
 * earliest in the wizard — the further back somebody is, the more they need
 * saying out loud. `since` is the oldest movement among them, which is the
 * figure that decides whether a company has stalled or is mid-signup.
 */
interface CompanyOnboarding {
  /** Members with an onboarding record at all. The rest predate it. */
  tracked: number
  done: number
  open: ProMember[]
  step: string
  since: string | null
}

function companyOnboarding(members: ProMember[]): CompanyOnboarding {
  const tracked = members.filter((m) => m.onboardingCompleted !== null)
  const open = tracked.filter((m) => !m.onboardingCompleted)
  const tally = new Map<string, number>()
  for (const m of open) {
    if (m.onboardingStep) tally.set(m.onboardingStep, (tally.get(m.onboardingStep) ?? 0) + 1)
  }
  const rank = (k: string) => {
    const i = STEPS.findIndex((s) => s.key === k)
    return i === -1 ? STEPS.length : i
  }
  const step =
    [...tally.entries()].sort((a, b) => b[1] - a[1] || rank(a[0]) - rank(b[0]))[0]?.[0] ?? ''
  const since = open
    .map((m) => m.onboardingUpdatedAt)
    .filter((d): d is string => Boolean(d))
    .sort()[0] ?? null
  return { tracked: tracked.length, done: tracked.length - open.length, open, step, since }
}

/**
 * The same rollup as a badge, for the collapsed row.
 *
 * Onboarding used to be visible only after expanding a company and reading down
 * a column of guides, which is the wrong way round: where a company stopped is
 * the reason to open it, not something you find once you have.
 */
function CompanyOnboardingBadge({ onb }: { onb: CompanyOnboarding }) {
  if (onb.tracked === 0) {
    return (
      <span
        className="procomp__meta"
        title="No onboarding record for any guide here — the accounts either predate onboarding or have no user document."
      >
        —
      </span>
    )
  }
  const many = onb.tracked > 1
  const count = many ? ` · ${onb.done}/${onb.tracked}` : ''
  if (onb.open.length === 0) {
    return (
      <span className="procomp__onbsum procomp__onbsum--done" title="Every guide finished the wizard.">
        Onboarded{count}
      </span>
    )
  }
  const detail = onb.open
    .map(
      (m) =>
        `${m.name || m.email || m.uid} — ${
          m.onboardingStep ? `stopped at ${stepLabel(m.onboardingStep)}` : 'not finished'
        }${m.onboardingUpdatedAt ? `, last moved ${formatDate(m.onboardingUpdatedAt)}` : ''}`,
    )
    .join('\n')
  return (
    <span
      className="procomp__onbsum procomp__onbsum--open"
      title={`${onb.step ? stepOf(onb.step)?.hint ?? '' : ''}\n${detail}`.trim()}
    >
      {onb.step ? `Stopped: ${stepLabel(onb.step)}` : 'Not finished'}
      {count}
    </span>
  )
}

/**
 * The rollup in a sentence, for the expanded row.
 *
 * The badge above has one line to work with; here there is room to say who is
 * stuck and when they last moved, which is what decides whether a company is
 * mid-signup or has walked away.
 */
function OnboardingSummary({ onb }: { onb: CompanyOnboarding }) {
  if (onb.tracked === 0) return <span className="muted">no record for any guide here</span>
  if (onb.open.length === 0) return <span>all {onb.tracked} finished</span>
  return (
    <span>
      {onb.done}/{onb.tracked} finished ·{' '}
      <span className="procomp__onb procomp__onb--open">
        {onb.open.length === 1
          ? `${onb.open[0]!.name || onb.open[0]!.email || 'one guide'} stopped at ${
              onb.open[0]!.onboardingStep ? stepLabel(onb.open[0]!.onboardingStep) : 'an unknown step'
            }`
          : `${onb.open.length} stopped, most at ${onb.step ? stepLabel(onb.step) : 'no step'}`}
      </span>
      {onb.since && ` · last moved ${formatDate(onb.since)}`}
    </span>
  )
}

/**
 * The billing profile, whole — and its absence, said out loud.
 *
 * This is the only place in the tool where a Skida PRO customer's own details
 * are visible: the legal name they trade under, where they are, what the VAT
 * check made of them. It is also the last step before paying, so a company
 * without one never got that far, and saying so is more useful than an empty
 * panel.
 */
function BillingCard({ billing }: { billing: ProBillingProfile | null }) {
  if (!billing) {
    return (
      <div className="procomp__billbox procomp__billbox--none">
        <div className="procomp__billhead">Billing profile</div>
        <p className="muted">Never filled in — this company has not reached the billing step.</p>
      </div>
    )
  }
  const place = [billing.postalCode, billing.city].filter(Boolean).join(' ')
  return (
    <div className="procomp__billbox">
      <div className="procomp__billhead">
        Billing profile
        {billing.updatedAt && (
          <span className="procomp__billwhen">saved {formatDate(billing.updatedAt)}</span>
        )}
      </div>
      <dl className="procomp__bill">
        {billing.companyName && (
          <>
            <dt>Legal name</dt>
            <dd>{billing.companyName}</dd>
          </>
        )}
        {(billing.addressLines.length > 0 || place || billing.countryName) && (
          <>
            <dt>Address</dt>
            <dd>
              {billing.addressLines.map((l) => (
                <div key={l}>{l}</div>
              ))}
              {place && <div>{place}</div>}
              {(billing.countryName || billing.countryCode) && (
                <div>{billing.countryName || billing.countryCode}</div>
              )}
            </dd>
          </>
        )}
        {billing.vatNumber && (
          <>
            <dt>VAT</dt>
            <dd>
              <code>{billing.vatNumber}</code>
              {billing.vatStatus && (
                <span className={`procomp__vat procomp__vat--${billing.vatStatus}`}>
                  {VAT_STATUS[billing.vatStatus] ?? billing.vatStatus}
                </span>
              )}
              {billing.vatName && <div className="muted">{billing.vatName}</div>}
            </dd>
          </>
        )}
        {billing.taxLabel && (
          <>
            <dt>Tax</dt>
            <dd title={billing.taxType}>
              {billing.taxLabel}
              {billing.reverseCharge && ' · reverse charge'}
            </dd>
          </>
        )}
      </dl>
    </div>
  )
}

/** The VAT check's own vocabulary, in words a rep can read. */
const VAT_STATUS: Record<string, string> = {
  valid: 'validated',
  invalid: 'not valid',
  not_applicable: 'outside the VAT area',
  error: 'check failed',
}

/**
 * Onboarding as one cell.
 *
 * A completed run is a date and nothing more. An unfinished one names the step
 * it stopped at, because that is what a follow-up conversation is actually
 * about. Profile completeness rides alongside either way — finishing the wizard
 * and filling the profile are different things, and a guide can do one without
 * the other.
 */
function Onboarding({ m }: { m: ProMember }) {
  const profile = `${m.profileFilled}/${m.profileTotal} profile`
  if (m.onboardingCompleted === null) {
    return <span className="muted" title="No onboarding record — this account predates it.">—</span>
  }
  if (m.onboardingCompleted) {
    return (
      <span className="procomp__onb procomp__onb--done" title={profile}>
        {m.onboardedAt ? formatDate(m.onboardedAt) : 'Done'} · {m.profileFilled}/{m.profileTotal}
      </span>
    )
  }
  return (
    <span
      className="procomp__onb procomp__onb--open"
      title={[
        m.onboardingStep ? stepOf(m.onboardingStep)?.hint : '',
        m.onboardingUpdatedAt ? `Last moved ${formatDate(m.onboardingUpdatedAt)}.` : '',
        profile,
      ]
        .filter(Boolean)
        .join('\n')}
    >
      {m.onboardingStep ? `Stopped: ${stepLabel(m.onboardingStep)}` : 'Not finished'} ·{' '}
      {m.profileFilled}/{m.profileTotal}
    </span>
  )
}

/** Four states, named for where the guide is with us rather than for billing. */
const ACCESS: Record<ProMember['access'], { label: string; hint: string }> = {
  registered: {
    label: 'Registered',
    hint: 'In a company, but with no Skida PRO access of any kind.',
  },
  granted: { label: 'Granted', hint: 'A live PRO promo grant, with its own expiry.' },
  expired: { label: 'Expired', hint: 'A PRO grant that has run out.' },
  paid: {
    label: 'Paid',
    hint: 'An ePay company agreement in state SUCCESS, still inside its period.',
  },
}

/**
 * A checkout that produced no access. Shown beside the badge rather than as one,
 * because it is not a level of access — it is a sales signal, and the warmest
 * one on this screen: somebody opened the payment window and stopped.
 */
const CHECKOUT: Record<'pending' | 'cancelled' | 'lapsed', { label: string; hint: string }> = {
  pending: {
    label: 'checkout not finished',
    hint: 'The ePay payment window was opened and never completed — no agreement exists. Worth a call.',
  },
  cancelled: { label: 'checkout cancelled', hint: 'The ePay agreement was cancelled.' },
  lapsed: { label: 'agreement ended', hint: 'A paid agreement whose period has passed.' },
}

function MemberRow({ m, place, showCompany = false }: { m: ProMember; place?: Place; showCompany?: boolean }) {
  return (
    <div className={`procomp__grid procomp__row${showCompany ? ' procomp__grid--guides' : ''}`}>
      <div className="usertable__who">
        <div className="usertable__name">{m.name || '(no name)'}</div>
        <div className="usertable__email">{m.email || m.uid}</div>
      </div>
      <div>
        <Flag code={place?.country} />
      </div>
      <div>
        <LanguageFlag language={place?.language} />
      </div>
      {showCompany && <div className="muted">{m.companyName}</div>}
      <div>
        <span className={`pill procomp__role procomp__role--${m.role}`}>{m.role}</span>
      </div>
      {!showCompany && <div className="muted">{m.joinedAt ? formatDate(m.joinedAt) : '—'}</div>}
      <div className="muted">{m.createdAt ? formatDate(m.createdAt) : '—'}</div>
      <div className="muted">{m.lastSignInAt ? formatDate(m.lastSignInAt) : '—'}</div>
      <div className="usertable__num">{m.routes ?? '—'}</div>
      <div className="usertable__num">{m.tours ?? '—'}</div>
      <div>
        <Onboarding m={m} />
      </div>
      <div>
        <span className={`pill procomp__plan procomp__plan--${m.access}`} title={ACCESS[m.access].hint}>
          {ACCESS[m.access].label}
        </span>
        {m.accessEndsAt && (
          <span className="procomp__until">
            {m.access === 'expired' ? 'ended ' : 'to '}
            {formatDate(m.accessEndsAt)}
            {m.accessCode && ` · ${m.accessCode}`}
          </span>
        )}
        {m.checkout && (
          <span
            className={`procomp__checkout procomp__checkout--${m.checkout}`}
            title={CHECKOUT[m.checkout].hint}
          >
            {CHECKOUT[m.checkout].label}
            {m.checkoutAmount && ` · ${m.checkoutAmount}`}
          </span>
        )}
        {m.consumerPremium && (
          <span className="procomp__until" title="Personal Skida Premium in the consumer app — a different product from Skida PRO.">
            + consumer Premium
          </span>
        )}
      </div>
    </div>
  )
}

function StateBadge({ state }: { state: ProState | null }) {
  if (!state) return <span className="procomp__meta">…</span>
  const st = STATE_OF[state]
  return (
    <span className="pill procomp__state" style={{ color: st.color }} title={st.hint}>
      {st.label}
    </span>
  )
}
